import { type RefObject, useEffect } from "react";
import type { MeetingClock } from "./meetingTimeline";

/**
 * Seek the player to `seconds` (meeting time) as soon as its active track has
 * metadata.
 *
 * RecordingPlayer assigns `videoRef` asynchronously, once the recordings load
 * and the active <video> mounts, so this polls on animation frames (up to about
 * ten seconds) instead of seeking once and missing it. `resetKey` restarts the
 * wait, for when the set of tracks changes underneath.
 *
 * Used by a task's meeting playback (the task's moment) and by the meeting page
 * (a meeting link's `t`).
 */
export function useSeekWhenReady(
  videoRef: RefObject<MeetingClock | null>,
  seconds: number | null | undefined,
  ready: boolean,
  resetKey?: unknown,
) {
  useEffect(() => {
    if (seconds == null || !ready) return;
    const target = Math.max(0, seconds);
    let done = false;
    let frames = 0;
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      if (v && v.readyState >= 1) {
        v.currentTime = target;
        done = true;
        return;
      }
      if (!done && frames++ < 600) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      done = true;
      cancelAnimationFrame(raf);
    };
  }, [videoRef, seconds, ready, resetKey]);
}
